/**
 * Omnichannel conversations API — served by the fasto_bi service backend
 * under `/svc/crm/conversations/*` (dedicated BI database, not Django).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAccessToken } from '@/api/client';
import { svcGet as get, svcPost as post } from '../svc/client';
import { svcClient } from '../svc/client';

export type CrmConversationChannel =
  | 'gmail'
  | 'whatsapp_customer'
  | 'instagram'
  | 'facebook_messenger';

export type CrmConversationStatus =
  | 'bot_handling'
  | 'awaiting_agent'
  | 'agent_active'
  | 'closed';

export interface CrmContactIdentity {
  id: number;
  channel: string;
  external_id: string;
  display_name: string;
  email: string;
  phone: string;
  user_id: number | null;
}

export interface CrmMediaItem {
  kind?: string;
  filename?: string;
  mime_type?: string;
  caption?: string;
  url?: string;
  /** Set once the media pipeline stored the bytes locally. */
  path?: string;
  size?: number;
  transcript?: string;
  download_error?: string;
  latitude?: number;
  longitude?: number;
  name?: string;
  address?: string;
}

export interface CrmMessage {
  id: number;
  conversation_id: number;
  direction: 'in' | 'out';
  sender_type: 'customer' | 'ai' | 'agent' | 'system';
  sender_user_id: number | null;
  body: string;
  media: CrmMediaItem[];
  external_id: string;
  delivery_status: string;
  delivery_error: string;
  ai_meta: Record<string, unknown>;
  created_at: string | null;
}

/** Auth-gated attachment URL (token via query — <img> can't set headers). */
export function crmMediaUrl(messageId: number, index: number): string {
  const token = getAccessToken();
  return `/svc/crm/media/${messageId}/${index}${token ? `?token=${encodeURIComponent(token)}` : ''}`;
}

export interface CrmConversation {
  id: number;
  channel: CrmConversationChannel | string;
  status: CrmConversationStatus | string;
  subject: string;
  contact: CrmContactIdentity | null;
  order_id: number | null;
  store_id: number | null;
  assignee_id: number | null;
  linked_ticket_id: number | null;
  unread_count: number;
  last_message_at: string | null;
  last_customer_message_at: string | null;
  ai_paused: boolean;
  pending_draft: {
    draft?: string;
    intent?: string;
    confidence?: number;
    rationale?: string;
    created_at?: string;
  };
  created_at: string | null;
  updated_at: string | null;
  closed_at: string | null;
  messages?: CrmMessage[];
}

export function useCrmConversations(params?: {
  channel?: string;
  status?: string;
  assignee?: string;
}) {
  const qs = new URLSearchParams();
  if (params?.channel) qs.set('channel', params.channel);
  if (params?.status) qs.set('status', params.status);
  if (params?.assignee) qs.set('assignee', params.assignee);
  const suffix = qs.toString() ? `?${qs}` : '';
  return useQuery({
    queryKey: ['bi', 'crm', 'conversations', params ?? {}],
    queryFn: () => get<{ results: CrmConversation[] }>(`/svc/crm/conversations/${suffix}`),
    refetchInterval: 15_000,
  });
}

export function useCrmConversation(id: number | null) {
  return useQuery({
    queryKey: ['bi', 'crm', 'conversations', id],
    queryFn: () => get<CrmConversation>(`/svc/crm/conversations/${id}/`),
    enabled: id != null && id > 0,
    refetchInterval: 10_000,
  });
}

export function useSendConversationMessage(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) =>
      post<CrmMessage>(`/svc/crm/conversations/${id}/messages/`, { body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['bi', 'crm', 'conversations'] });
    },
  });
}

export function useApproveDraft(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body?: string) =>
      post<CrmConversation>(`/svc/crm/conversations/${id}/ai/approve-draft/`, {
        body: body ?? undefined,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['bi', 'crm', 'conversations'] });
    },
  });
}

export function useDismissDraft(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post(`/svc/crm/conversations/${id}/ai/dismiss-draft/`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['bi', 'crm', 'conversations'] });
    },
  });
}

export function useConversationTakeover(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post(`/svc/crm/conversations/${id}/takeover/`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['bi', 'crm', 'conversations'] });
    },
  });
}

export function useConversationResumeAi(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post(`/svc/crm/conversations/${id}/resume-ai/`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['bi', 'crm', 'conversations'] });
    },
  });
}

export function useCreateConversationTicket(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (summary?: string) =>
      post<{ ticket_id: number }>(`/svc/crm/conversations/${id}/create-ticket/`, {
        summary: summary ?? '',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['bi', 'crm'] });
    },
  });
}

export function useCloseConversation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post(`/svc/crm/conversations/${id}/close/`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['bi', 'crm', 'conversations'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Customer 360 / tagging
// ---------------------------------------------------------------------------

export interface CrmCustomerSummary {
  user_id: number;
  name: string;
  email: string;
  phone: string;
  joined_at: string | null;
  total_orders: number;
  matched_on?: string;
}

export interface CrmLinkedOrder {
  order_id: number;
  status: string;
  store_id: number | null;
  store_name: string;
  created_at: string | null;
  total?: number | null;
  payment_type?: string;
  is_payment_done?: boolean;
  delivery_address?: string;
  completed_at?: string | null;
}

export interface CrmCustomerContext {
  customer: CrmCustomerSummary | null;
  recent_orders: CrmLinkedOrder[];
  tagged_order: CrmLinkedOrder | null;
  suggestions: CrmCustomerSummary[];
  other_conversations: Array<{
    id: number;
    channel: string;
    status: string;
    subject: string;
    last_message_at: string | null;
  }>;
}

export function useCustomerContext(id: number | null) {
  return useQuery({
    queryKey: ['bi', 'crm', 'customer-context', id],
    queryFn: () => get<CrmCustomerContext>(`/svc/crm/conversations/${id}/customer-context/`),
    enabled: id != null && id > 0,
    staleTime: 15_000,
  });
}

export function useCustomerSearch(q: string) {
  return useQuery({
    queryKey: ['bi', 'crm', 'customer-search', q],
    queryFn: () =>
      get<{ results: CrmCustomerSummary[] }>(
        `/svc/crm/customers/search/?q=${encodeURIComponent(q)}`,
      ),
    enabled: q.trim().length >= 3,
    staleTime: 30_000,
  });
}

function useTagMutation(id: number, path: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown> = {}) =>
      post<CrmCustomerContext>(`/svc/crm/conversations/${id}/${path}/`, body),
    onSuccess: (data) => {
      qc.setQueryData(['bi', 'crm', 'customer-context', id], data);
      void qc.invalidateQueries({ queryKey: ['bi', 'crm', 'conversations'] });
    },
  });
}

export function useLinkCustomer(id: number) {
  return useTagMutation(id, 'link-customer');
}
export function useUnlinkCustomer(id: number) {
  return useTagMutation(id, 'unlink-customer');
}
export function useLinkOrder(id: number) {
  return useTagMutation(id, 'link-order');
}
export function useUnlinkOrder(id: number) {
  return useTagMutation(id, 'unlink-order');
}

// ---------------------------------------------------------------------------
// Smart compose + canned replies
// ---------------------------------------------------------------------------

export interface CrmSuggestedReply {
  ok: boolean;
  reply: string;
  intent: string;
  confidence: number;
}

export function useSuggestReply(id: number) {
  return useMutation({
    mutationFn: () => post<CrmSuggestedReply>(`/svc/crm/conversations/${id}/suggest-reply/`, {}),
  });
}

export interface CrmCannedReply {
  id: number;
  title: string;
  shortcut: string;
  body: string;
  channels: string[];
  is_active: boolean;
  updated_at: string | null;
}

export function useCannedReplies(includeInactive = false) {
  return useQuery({
    queryKey: ['bi', 'crm', 'canned-replies', includeInactive],
    queryFn: () =>
      get<{ results: CrmCannedReply[] }>(
        `/svc/crm/canned-replies/${includeInactive ? '?include_inactive=true' : ''}`,
      ),
    staleTime: 60_000,
  });
}

export function useCreateCannedReply() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<CrmCannedReply>) =>
      post<CrmCannedReply>('/svc/crm/canned-replies/', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['bi', 'crm', 'canned-replies'] }),
  });
}

export function usePatchCannedReply() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: number; body: Partial<CrmCannedReply> }) => {
      const r = await svcClient.patch<CrmCannedReply>(`/svc/crm/canned-replies/${id}/`, body);
      return r.data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['bi', 'crm', 'canned-replies'] }),
  });
}

export function useDeleteCannedReply() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await svcClient.delete(`/svc/crm/canned-replies/${id}/`);
      return id;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['bi', 'crm', 'canned-replies'] }),
  });
}

// ---------------------------------------------------------------------------
// Analytics
// ---------------------------------------------------------------------------

export interface CrmConversationAnalytics {
  days: number;
  overview: {
    total_conversations: number;
    bot_resolved: number;
    bot_resolved_pct: number;
    agent_handled: number;
    escalated: number;
    closed: number;
  };
  per_channel: Array<{ channel: string; conversations: number; closed: number; escalated: number }>;
  response_times: Array<{
    channel: string;
    median_ai_seconds: number | null;
    median_agent_seconds: number | null;
  }>;
  escalation_reasons: Array<{ intent: string; count: number }>;
  low_confidence_intents: Array<{ intent: string; drafts: number; avg_confidence: number }>;
  daily_volume: Array<{ day: string; channel: string; conversations: number }>;
  hourly_volume: Array<{ hour: number; messages: number }>;
  ordering_funnel: {
    carts_created: number;
    carts_converted: number;
    conversion_pct: number;
    nudges_sent: number;
  };
}

export function useConversationAnalytics(days = 30) {
  return useQuery({
    queryKey: ['bi', 'crm', 'conversation-analytics', days],
    queryFn: () => get<CrmConversationAnalytics>(`/svc/crm/analytics/conversations/?days=${days}`),
    staleTime: 60_000,
  });
}
